import Big from 'big.js'
import { z } from 'zod'

export const fieldCannotBeEmpty = 'common.forms.validation.field_cannot_be_empty'
export const fieldNotValid = 'common.forms.validation.field_not_valid'
export const fieldMustBeAtLeastNChars = 'common.forms.validation.field_must_be_at_least_n_chars'
export const fieldCannotBeMoreThanNChars =
  'common.forms.validation.field_cannot_be_more_than_n_chars'
export const fieldMustBeSelected = 'common.forms.validation.field_must_be_selected'

export const PHONE_VALUE_MIN_LENGTH = 6
export const PHONE_VALUE_MAX_LENGTH = 20

export const notRequiredSchema = z.preprocess(() => undefined, z.undefined().optional())

/**
 * Types a field that can parse to undefined, e.g. `cond ? schema : notRequiredSchema`, as an
 * optional key, as zod 3 inferred it: zod 4 keeps such a key required. Type-only, the schema is
 * returned unchanged.
 */
export const optionalField = <T extends z.ZodType>(schema: T) =>
  schema as unknown as z.ZodOptional<T>

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type TranslationFunc = (key: string, values?: Record<string, any>) => string

export type GetTranslationMessageParams = {
  key: string
  field?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  values?: Record<string, any>
  t?: TranslationFunc
}

export const getTranslationMessage = ({ key, field, values, t }: GetTranslationMessageParams) => {
  if (!t) {
    return undefined
  }

  return { message: t(key, { field: field ? t(field) : undefined, ...values }) }
}

type CreateNonEmptyStringSchemaParams = {
  field: string
  t?: TranslationFunc
  messageKey?: string
}

export const createNonEmptyStringSchema = ({
  field,
  t,
  messageKey,
}: CreateNonEmptyStringSchemaParams) => {
  const message = getTranslationMessage({
    key: messageKey ?? fieldCannotBeEmpty,
    field,
    t,
  })

  return z.string(message).trim().min(1, message)
}

export const createPhoneNumberSchema = (t?: TranslationFunc) => {
  const field = 'common.forms.labels.phone'
  const minMessage = getTranslationMessage({
    key: fieldMustBeAtLeastNChars,
    field,
    t,
    values: {
      n: PHONE_VALUE_MIN_LENGTH,
    },
  })
  const maxMessage = getTranslationMessage({
    key: fieldCannotBeMoreThanNChars,
    field,
    t,
    values: {
      n: PHONE_VALUE_MAX_LENGTH,
    },
  })
  return (
    z
      .object({
        value: z
          .string()
          .trim()
          .min(PHONE_VALUE_MIN_LENGTH, minMessage)
          .max(PHONE_VALUE_MAX_LENGTH, maxMessage),
        countryCode: z.string().optional(),
        dialingCode: z.number().optional(),
      })
      // prefault, not default: zod 4 does not validate a default, and a missing phone must fail
      .prefault({ value: '', countryCode: undefined, dialingCode: undefined })
  )
}

export type Phone = z.infer<ReturnType<typeof createPhoneNumberSchema>>

export const createDateSchema = (field?: string, t?: TranslationFunc) => {
  const message = getTranslationMessage({ key: fieldNotValid, field, t })

  return z.preprocess((arg) => {
    if (arg == null) {
      return undefined
    }

    if (arg instanceof Date) {
      return arg
    }

    if (typeof arg === 'string') {
      return new Date(arg)
    }

    throw new Error(`Cannot parse date ${JSON.stringify(arg)}`)
  }, z.date(message))
}

export const DateSchema = createDateSchema()

const isValidDateString = (arg: string) => !Number.isNaN(new Date(arg).valueOf())
export const ISODateStringSchema = z.preprocess((arg) => {
  if (arg == null) {
    return undefined
  }

  if (typeof arg === 'string' && isValidDateString(arg)) {
    return arg
  }

  throw new Error(`Cannot parse date string ${JSON.stringify(arg)}`)
}, z.string())

export const AddressSchema = z
  .object({
    cityName: z.string(),
    countryName: z.string(),
    countryCode: z.string(),
    stateOrProvince: z.string(),
    streetAddress: z.array(z.string()),
    zipCode: z.string(),
  })
  .partial()

export type Address = z.infer<typeof AddressSchema>

export const CarHireLocationSchema = z.object({
  name: z.string().optional(),
  iataCode: z.string().optional(),
  address: AddressSchema,
})

export type CarHireLocation = z.infer<typeof CarHireLocationSchema>

export const RoomSchema = z.object({
  name: z.string(),
  bedGroupId: z.string().optional(),
  dimensions: z
    .object({
      sqMeters: z.number().optional(),
      sqFeet: z.number().optional(),
    })
    .optional(),
  maxOccupants: z.number().optional(),
  beds: z.string(),
  count: z.number(),
})
export type Room = z.infer<typeof RoomSchema>

export const VehicleSchema = z.object({
  name: z.string(),
  vehicleClassName: z.string(),
})

export const AgeGroupSchema = z.enum(['ADULT', 'SENIOR', 'YOUTH', 'CHILD', 'INFANT', 'TRAVELER'])

export type AgeGroup = z.infer<typeof AgeGroupSchema>

export const TravelerGroupSchema = z.object({
  ageGroup: AgeGroupSchema,
  startAge: z.number().optional(),
  endAge: z.number().optional(),
  quantity: z.number(),
})

export type TravelerGroup = z.infer<typeof TravelerGroupSchema>

export const MonetaryAmountSchema = z.object({
  currency: z.string(),
  amount: z.number(),
})
export type MonetaryAmount = z.infer<typeof MonetaryAmountSchema>

export const TaxDetailsSchema = z.object({
  taxAmount: MonetaryAmountSchema,
  taxName: z.string(),
  taxCode: z.string().optional(),
})
export type TaxDetails = z.infer<typeof TaxDetailsSchema>

const PriceOptionTaxAmountSchema = z.object({
  amount: z.number(),
  currencyCode: z.string(),
})

const PriceOptionTaxDetailsSchema = z.object({
  taxAmount: PriceOptionTaxAmountSchema,
  taxName: z.string(),
  taxCode: z.string().optional(),
})

const BalanceBoosterMultiplierSchema = z.union([
  z.literal('1X'),
  z.literal('2X'),
  z.literal('3X'),
  z.literal('no_booster'),
])

export type BalanceBoosterMultiplier = z.infer<typeof BalanceBoosterMultiplierSchema>

export const BalanceBoosterSchema = z.object({
  multiplier: BalanceBoosterMultiplierSchema,
  code: z.string(),
  promoPaid: MonetaryAmountSchema,
  earnedPoints: z.number().nonnegative(),
})

export type BalanceBooster = z.infer<typeof BalanceBoosterSchema>

export type PriceOptionTaxDetails = z.infer<typeof PriceOptionTaxDetailsSchema>

export const HotelItemType = 'HOTEL'
export const ExperienceItemType = 'EXPERIENCE'
export const CarHireItemType = 'CAR_HIRE'
export const FlightItemType = 'FLIGHT'

export const CarHireLocationPolicy = 'location'
export const CarHireRentalPolicy = 'rental'
export const CarHireLocationRentalPolicy = 'locationRental'

export const CarHireAttributeFeature = 'features'
export const CarHireAttributeCapacity = 'capacity'

export const CarHireAttributeTypeDoor = 'Doors'
export const CarHireAttributeTypeSeats = 'Seats'
export const CarHireAttributeTypeSuitcase = 'Suitcase'
export const CarHireAttributeTypeAir = 'Air'

export const CarVendorAvis = 'avis'
export const CarVendorAvisIncludedDriver = 'Additional Driver'
export const CarVendorAvisTheftProtection = 'Theft Protection'
export const CarVendorAvisTheftProtectionEs = 'Protección contra Robo'
export const CarVendorAvisAdditionalProtectionInsurance = 'Additional Protection Insurance'
export const CarVendorAvisAdditionalProtectionInsuranceEs = 'Seguro de Protección Adicional'
export const CarVendorAvisAdditionalLiabilityInsurance = 'Additional Liability Insurance'
export const CarVendorAvisAdditionalLiabilityInsuranceEs =
  'Seguro de Responsabilidad Civil Adicional'
export const CarVendorAvisCollisionDamageWaiver = 'Collision Damage Waiver'
export const CarVendorAvisCollisionDamageWaiverEs =
  'Exención de Responsabilidad por Daños por Colisión (CDW)'
export const CarVendorAvisSuperCollisionDamageWaiver = 'Super Collision Damage Waiver'
export const CarVendorAvisSuperCollisionDamageWaiverEs =
  'Super Exención de Responsabilidad por Daños por Colisión (SCDW)'
export const CarVendorAvisPersonalAccidentInsurance = 'Personal Accident Insurance'
export const CarVendorAvisPersonalAccidentInsuranceEs = 'Seguro de Accidentes Personales'
export const CarVendorAvisSuperPersonalAccidentInsurance = 'Super Personal Accident Insurance'
export const CarVendorAvisSuperPersonalAccidentInsuranceEs = 'Super Seguro de Accidentes Personales'
export const CarVendorAvisPersonalEffectsProtection = 'Personal Effects Protection'
export const CarVendorAvisPersonalEffectsProtectionEs = 'Protección de Efectos Personales'
export const CarVendorAvisLossDamageWaiver = 'Loss Damage Waiver'
export const CarVendorAvisLossDamageWaiverEs = 'Exención de Responsabilidad por Daños o Pérdida'
export const CarVendorAvisSuperTheftProtection = 'Super Theft Protection'
export const CarVendorAvisSuperTheftProtectionEs = 'Super Protección contra Robo'
export const CarVendorAvisPackageProtection = 'Package Protection'
export const CarVendorAvisPackageProtectionEs = 'Paquete de Protección'

type SurchargesBreakdown = {
  feeCode?: string
  feeName?: string
  amount: Big
}

interface SurchargesBreakdownAndTotalSchema {
  total: Big
  surchargesBreakdown: SurchargesBreakdown[]
}

export const SurchargesMonetaryAmountSchema = MonetaryAmountSchema.extend({
  feeCode: z.string().optional(),
  feeName: z.string().optional(),
})

export type SurchargesBreakdownAndTotal = SurchargesBreakdownAndTotalSchema

export const OrderLineStatusSchema = z.enum([
  'DELIVERED',
  'IN_PROGRESS',
  'READY_TO_PROCEED',
  'REMOVED',
  'SUSPENDED',
  'UNABLE_TO_DELIVER',
  'FAILED_TO_DELIVER',
  'UNKNOWN',
])
export type OrderLineStatus = z.infer<typeof OrderLineStatusSchema>

const MeasurementSchema = z.object({
  unit: z.string(),
  value: z.number(),
})

const FlightServicesSchema = z.object({
  flightServiceType: z.string().optional(),
  allowance: z.object({
    quantity: z.number(),
    measures: z.array(MeasurementSchema),
  }),
})

export type FlightService = z.infer<typeof FlightServicesSchema>

export const FlightSegmentSchema = z.object({
  flightSegmentId: z.string(),
  airlineIataCode: z.string(),
  operatingAirlineIataCode: z.string().optional(),
  flightNumber: z.string(),
  flightClass: z.string(),

  departureIataCode: z.string(),
  departureAirportName: z.string(),
  departureTerminal: z.string().optional(),
  departureDateTime: z.string(),

  arrivalIataCode: z.string(),
  arrivalAirportName: z.string(),
  arrivalTerminal: z.string().optional(),
  arrivalDateTime: z.string(),

  flightDuration: z.number(),
  services: z.array(FlightServicesSchema).optional(),
})

export type FlightSegment = z.infer<typeof FlightSegmentSchema>

export const FlightJourneySchema = z.object({
  durationInMinutes: z.number(),
  segments: z.array(FlightSegmentSchema),
})
export type FlightJourney = z.infer<typeof FlightJourneySchema>

export const CarHirePolicySchema = z.object({
  policyType: z.string().optional(),
  policyLink: z.string().optional(),
})

export type CarHirePolicy = z.infer<typeof CarHirePolicySchema>

export const ExperienceCancellationPolicySchema = z.object({
  cancelIfBadWeather: z.boolean(),
  cancelIfInsufficientTravelers: z.boolean(),
})

export type ExperienceCancellationPolicy = z.infer<typeof ExperienceCancellationPolicySchema>

export const ExperienceDurationSchema = z.object({
  duration: z.number().optional(),
  minDuration: z.number().optional(),
  maxDuration: z.number().optional(),
  freeText: z.string().optional(),
  uom: z.string().optional(),
})

export const ExperienceLocationSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
  address: AddressSchema.optional(),
})

export type ExperienceLocation = z.infer<typeof ExperienceLocationSchema>

export const PromotionsTransactionTypeSchema = z.enum([
  'BONUS_AVIOS',
  'ENHANCED_EARN_RATE',
  'BALANCE_BOOSTER',
])

export type PromotionsTransactionType = z.infer<typeof PromotionsTransactionTypeSchema>
